// Re-export the components from react-menu in case we want to modify them later
// without changing downstream imports (proxy pattern).
/* eslint react-refresh/only-export-components: 0 */
import "./Menu.scss";

import {
  type ClickEvent,
  ControlledMenuProps,
  type EventHandler,
  MenuItemProps,
  MenuProps,
  ControlledMenu as ReactControlledMenu,
  Menu as ReactMenu,
  MenuItem as ReactMenuItem,
  SubMenu as ReactSubMenu,
  SubMenuProps,
} from "@szhsin/react-menu";
import clsx from "clsx";
import { type FC, MouseEvent } from "react";

import { ChevronRightIcon } from "mds/icons";
import { type AmplitudeEventPropertiesType, trackEvent } from "utils/amplitude";

export {
  FocusableItem,
  MenuDivider,
  MenuGroup,
  MenuRadioGroup,
  MenuHeader,
  useClick,
  type MenuProps,
  type MenuDirection,
  type ClickEvent,
  type MenuInstance,
  type MenuChangeEvent,
} from "@szhsin/react-menu";

type MdsMenuProps = MenuProps & {
  limitItemHeight?: boolean;
};

/**
 * A simple wrapper around the `@szhsin/react-menu` `Menu` component that sets some default props
 * and css classes for MDS styling.
 * Follow the documentation in https://szhsin.github.io/react-menu/ for usage.
 * Components like MenuItem, MenuDivider, and SubMenu should be imported from @szhsin/react-menu
 * directly and uses as is. They will be styles by this parent component.
 */
export const Menu: FC<MdsMenuProps> = ({ className, limitItemHeight = true, ...menuProps }) => {
  return (
    <ReactMenu
      align="center"
      className={clsx(
        "mds-menu",
        !menuProps.children && "empty",
        limitItemHeight && "limit-item-height",
        className,
      )}
      direction="bottom"
      overflow="auto"
      position="auto"
      viewScroll="auto"
      portal
      {...menuProps}
    >
      {menuProps.children}
    </ReactMenu>
  );
};

type SubMenuArrowProps = SubMenuProps & { withArrow?: boolean; iconColor?: string };

/**
 * A simple wrapper around the SubMenu allowing for some additional styling
 */
export const SubMenu: FC<SubMenuArrowProps & { allowPropagation?: boolean }> = ({
  label,
  withArrow,
  children,
  className,
  onClick,
  allowPropagation = false,
  ...subMenuProps
}) => {
  const labelWithArrow = withArrow ? (
    <>
      {label}
      <span className="menu__withArrow">
        <ChevronRightIcon size="s" />
      </span>
    </>
  ) : (
    label
  );

  const onClickHandler = (e: MouseEvent<HTMLElement>) => {
    if (!allowPropagation) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <ReactSubMenu
      className={clsx("interactive", className)}
      direction="right"
      label={labelWithArrow}
      overflow="auto"
      onClick={onClickHandler}
      {...subMenuProps}
    >
      {children}
    </ReactSubMenu>
  );
};

export type MenuItemKind =
  | "destructive"
  | "instructional"
  | "radio"
  | "page-dropdown"
  | "topic-dropdown";

type MenuItemKindProps = MenuItemProps & {
  eventName?: string;
  eventProps?: Omit<AmplitudeEventPropertiesType, "eventCategory">;
  kind?: MenuItemKind;
  iconColor?: string;
  download?: string;
  allowPropagation?: boolean;
};

/**
 * A simple wrapper around the SubMenu allowing for some additional styling
 */
export const MenuItem: FC<MenuItemKindProps> = ({
  kind,
  className,
  eventName,
  eventProps,
  onClick,
  allowPropagation = false,
  ...menuItemProps
}) => {
  const clickHandler: EventHandler<ClickEvent> | undefined =
    eventName || onClick
      ? (e) => {
          if (eventName) {
            trackEvent(eventName, { eventCategory: "Button press", ...eventProps });
          }
          if (!allowPropagation) {
            e.syntheticEvent.stopPropagation();
            e.syntheticEvent.preventDefault();
          }

          if (onClick) {
            onClick(e);
          }
        }
      : undefined;

  return (
    <ReactMenuItem
      className={clsx(
        "mds-menu interactive",
        kind && `szh-menu__item-${kind}`,
        kind === "radio" &&
          "before:size-4 before:rounded-full before:border before:border-black-tint-40 before:bg-black-tint-99 " +
            "aria-checked:before:size-2 aria-checked:before:border-[5px] aria-checked:before:border-blue",
        className,
      )}
      onClick={clickHandler}
      {...menuItemProps}
    >
      {menuItemProps.children}
    </ReactMenuItem>
  );
};

/**
 * See Menu. This is the equivalent for ControlledMenu.
 */
export const ControlledMenu: FC<ControlledMenuProps> = ({ className, ...menuProps }) => {
  return (
    <ReactControlledMenu
      align="center"
      className={clsx("mds-menu", className)}
      direction="bottom"
      overflow="auto"
      position="auto"
      viewScroll="auto"
      {...menuProps}
    >
      {menuProps.children}
    </ReactControlledMenu>
  );
};

export type MenuType = "menu" | "submenu" | "controlledmenu";
